<template>
  <section id="download" data-aos="fade" class="bg-white py-24 sm:py-32">
    <div class="mx-auto max-w-7xl px-6 lg:px-8">
      <div data-aos="fade-up" class="mx-auto max-w-2xl lg:mx-0">
        <h2
          class="text-4xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-5xl"
        >
          Getting started is <TextUnderline>quick</TextUnderline> and
          <TextUnderline>easy</TextUnderline>
        </h2>
        <p class="mt-6 text-base/7 text-gray-600">
          Whether you’re a first-time user or an experienced educator, the
          Juniper App helps you connect with your school community quickly and
          easily.
        </p>
      </div>

      <div
        class="mx-auto mt-16 flex max-w-2xl flex-col gap-8 lg:mx-0 lg:mt-20 lg:max-w-none lg:flex-row lg:items-end"
      >
        <!-- Step 1 -->
        <div
          data-aos="zoom-in-up"
          class="flex flex-col-reverse justify-between gap-x-16 gap-y-8 rounded-2xl bg-gray-100 p-8 sm:w-3/4 sm:max-w-md sm:flex-row-reverse sm:items-end lg:w-72 lg:max-w-none lg:flex-none lg:flex-col lg:items-start"
        >
          <p class="flex-none text-3xl font-bold tracking-tight text-gray-900">
            Step 1
          </p>
          <div class="sm:w-80 sm:shrink lg:w-auto lg:flex-none">
            <p class="text-lg font-semibold tracking-tight text-gray-900">
              Receive your school invite
            </p>
            <p class="mt-2 text-base/7 text-gray-600">
              Your school admin will send a secure invitation to your email.
              Follow the link to create your teacher account and verify your
              details.
            </p>
          </div>
        </div>

        <!-- Step 2 -->
        <div
          data-aos="zoom-in-up"
          class="flex flex-col-reverse justify-between gap-x-16 gap-y-8 rounded-2xl bg-purple-950 p-8 sm:flex-row-reverse sm:items-end lg:w-full lg:max-w-sm lg:flex-auto lg:flex-col lg:items-start lg:gap-y-44"
        >
          <p class="flex-none text-3xl font-bold tracking-tight text-white">
            Step 2
          </p>
          <div class="sm:w-80 sm:shrink lg:w-auto lg:flex-none">
            <p class="text-lg font-semibold tracking-tight text-white">
              Download the Juniper Education app
            </p>
            <p class="mt-2 text-base/7 text-gray-200">
              Get it from the App Store or Google Play to manage your classroom
              and communicate with parents.
            </p>
            <ul class="mt-6 flex flex-col gap-2">
              <li>
                <a
                  href="https://apps.apple.com/ie/app/juniper-education/id6514314038"
                  @click="trackAppStoreClick"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="rounded-md bg-gray-100 transition-colors px-3.5 py-2.5 text-sm font-semibold text-gray-800 shadow-xs flex gap-2 items-center hover:bg-gray-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-200"
                >
                  <i class="fa-brands fa-apple w-10 text-3xl"></i>
                  <div class="flex flex-col">
                    <small>Download from the</small>
                    <span>App Store</span>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href="https://play.google.com/store/apps/details?id=com.juniper.mobile.app"
                  @click="trackGooglePlayClick"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="rounded-md bg-green-600 transition-colors px-3.5 py-2.5 text-sm font-semibold text-white shadow-xs flex gap-2 items-center hover:bg-green-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
                >
                  <i class="fa-brands fa-android w-10 text-3xl"></i>
                  <div class="flex flex-col">
                    <small>Download from</small>
                    <span>Google Play</span>
                  </div>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <!-- Step 3 -->
        <div
          data-aos="zoom-in-up"
          class="flex flex-col-reverse justify-between gap-x-16 gap-y-8 rounded-2xl bg-purple-600 p-8 sm:w-11/12 sm:max-w-xl sm:flex-row-reverse sm:items-end lg:w-full lg:max-w-none lg:flex-auto lg:flex-col lg:items-start lg:gap-y-28"
        >
          <p class="flex-none text-3xl font-bold tracking-tight text-white">
            Step 3
          </p>
          <div class="sm:w-80 sm:shrink lg:w-auto lg:flex-none">
            <p class="text-lg font-semibold tracking-tight text-white">
              Sign in & start using your tools
            </p>
            <p class="mt-2 text-base/7 text-indigo-200">
              Log in with your school email and password to access your class
              lists, share updates, and stay connected with parents.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import TextUnderline from "./TextUnderline.vue";
import { track } from "@vercel/analytics";

const trackAppStoreClick = () => {
  track("download_app", {
    platform: "ios",
    page: "teacher",
    component: "download",
  });
};

const trackGooglePlayClick = () => {
  track("download_app", {
    platform: "android",
    page: "teacher",
    component: "download",
  });
};
</script>
